import { useState } from 'react';
import { logger } from '@fiverr-private/obs';
import { getContext } from '@fiverr-private/fiverr_context';
import type { Currency as CurrencyType } from '@fiverr-private/currencies_plugin';
import { type ExternalTriggerParams, triggerSignUp } from '@fiverr-private/identification_triggers';
import { getMiraRecruiterEntry, type MiraSearchParams } from './helpers';
// backward-compatibility
export type { CurrencyType as Currency };

export const OPEN_DRAWER_EVENT_NAME = 'openBriefCreationDrawer';
export const WINDOW_INDICATION = 'isBriefCreationDrawerLoaded';

export const isDrawerMounted = () => !!window[WINDOW_INDICATION];

export interface DrawerProps {
    id?: string;
    source: string;
    currency: CurrencyType;
    isOpen: boolean;
    setIsLoading?: (isLoading: boolean) => void;
    onClose?: (result: any) => void;
    initialPrompt?: string;
    onOpen?: () => void;
    identificationModalOptions?: ExternalTriggerParams['modalOptions'];
}

export type OpenDrawerParams = Omit<DrawerProps, 'isOpen'> & {
    isInMiraAgenticBriefsRollout?: boolean;
    miraSearchParams?: MiraSearchParams;
};

export const openBriefCreationDrawer = async ({
    currency,
    source,
    setIsLoading,
    id,
    onClose,
    initialPrompt,
    onOpen,
    identificationModalOptions,
    isInMiraAgenticBriefsRollout,
    miraSearchParams,
}: OpenDrawerParams) => {
    const openBriefsDrawer = () => {
        if (!isDrawerMounted()) {
            logger.error('Creation drawer is not mounted');
            return false;
        }

        const event = new CustomEvent<DrawerProps>(OPEN_DRAWER_EVENT_NAME, {
            detail: {
                id,
                source,
                currency,
                isOpen: true,
                setIsLoading,
                onClose,
                initialPrompt,
                onOpen,
            },
        });
        window.dispatchEvent(event);
    };

    try {
        const { userId } = getContext();
        if (!userId) {
            await triggerSignUp({
                modalOptions: {
                    source: 'brief_creation_triggers',
                    triggerId: 'brief_creation_triggers',
                    ...identificationModalOptions,
                },
            });
            return false;
        } else if (isInMiraAgenticBriefsRollout) {
            getMiraRecruiterEntry(miraSearchParams).openMiraTab();
            return false;
        } else {
            openBriefsDrawer();
        }
    } catch (error) {
        logger.error('Failed to handle click');
    }

    return true;
};

export const useOpenBriefCreationDrawer = (id?: string) => {
    const [isLoading, setIsLoading] = useState(false);

    const openBriefCreationDrawerInternal = ({
        currency,
        source,
        onClose,
        initialPrompt,
        onOpen,
        identificationModalOptions,
        isInMiraAgenticBriefsRollout,
        miraSearchParams,
    }: OpenDrawerParams) => {
        openBriefCreationDrawer({
            currency,
            source,
            id,
            setIsLoading,
            onClose,
            initialPrompt,
            onOpen,
            identificationModalOptions,
            isInMiraAgenticBriefsRollout,
            miraSearchParams,
        });
    };

    return { openBriefCreationDrawer: openBriefCreationDrawerInternal, isLoading };
};
